import { createReducer } from '@reduxjs/toolkit';
import { ArticleDto, GoodsDto } from '../../api/ClientApi';
import { myAssert } from '../../common/console';

import {
    createNewEmptyBareGoodsEntity,
    clearBareGoods,
    updateBareGoodsArticle,
    updateBareGoodsAvailableQuantity,
    updateBareGoodsOrderedQuantity,
    removeBareGoodsEntity,
    setBareGoodsGoodsList,
    addNewBareGoodsEntity,
    setBareGoodsEntities,
} from '../actions/orderActions';
import { clearBaseEntityState, IBaseEntityState, SaveEntityStatus } from './common';

export type BareGoodsEntity = {
    index: number;
    article?: ArticleDto;
    availableQuantity: number;
    orderedQuantity: number;
    goods: GoodsDto[];
};

export interface GoodsToOrderFormReducerState extends IBaseEntityState {
    bareGoodsEntities: BareGoodsEntity[] | null;
}

function clearState(state: GoodsToOrderFormReducerState): void {
    state.bareGoodsEntities = null;

    clearBaseEntityState(state);
}

function getInitialState(): GoodsToOrderFormReducerState {
    const state: GoodsToOrderFormReducerState = {
        bareGoodsEntities: null,
        baseJsonString: null,
        currentJsonString: null,
        isChanged: undefined,
        saveStatus: SaveEntityStatus.None,
        modified: undefined,
    };

    clearState(state);

    return state;
}

export const goodsToOrderFormReducer = createReducer<GoodsToOrderFormReducerState>(
    getInitialState(), // Initial state
    (builder) => {
        builder
            .addCase(createNewEmptyBareGoodsEntity, (state: GoodsToOrderFormReducerState, action: any) => {
                const index =
                    !!state.bareGoodsEntities && state.bareGoodsEntities.length > 0
                        ? state.bareGoodsEntities[state.bareGoodsEntities.length - 1].index + 1
                        : 1;

                const newBareGoods: BareGoodsEntity = {
                    index: index,
                    article: undefined,
                    availableQuantity: 0,
                    orderedQuantity: 0,
                    goods: [],
                };

                if (!state.bareGoodsEntities) {
                    state.bareGoodsEntities = [newBareGoods];
                } else {
                    state.bareGoodsEntities = [...state.bareGoodsEntities, newBareGoods];
                }
            })
            .addCase(addNewBareGoodsEntity, (state: GoodsToOrderFormReducerState, action: any) => {
                const index =
                    !!state.bareGoodsEntities && state.bareGoodsEntities.length > 0
                        ? state.bareGoodsEntities[state.bareGoodsEntities.length - 1].index + 1
                        : 1;

                const newBareGoods: BareGoodsEntity = action.payload;

                newBareGoods.index = index;

                if (!state.bareGoodsEntities) {
                    state.bareGoodsEntities = [newBareGoods];
                } else {
                    state.bareGoodsEntities = [...state.bareGoodsEntities, newBareGoods];
                }
            })
            .addCase(setBareGoodsEntities, (state: GoodsToOrderFormReducerState, action: any) => {
                const newBareGoodsEntities = action.payload;

                state.bareGoodsEntities = newBareGoodsEntities ?? [];
            })
            .addCase(clearBareGoods, (state: GoodsToOrderFormReducerState, action: any) => {
                clearState(state);
            })
            .addCase(removeBareGoodsEntity, (state: GoodsToOrderFormReducerState, action: any) => {
                if (!!state.bareGoodsEntities) {
                    const index = action.payload;
                    if (state.bareGoodsEntities.length === 0) {
                        state.bareGoodsEntities = [];
                    } else {
                        const goodIndex = state.bareGoodsEntities.findIndex((g) => g.index === index);
                        if (goodIndex > -1) {
                            state.bareGoodsEntities.splice(goodIndex, 1);
                        }
                    }
                }
            })
            .addCase(updateBareGoodsArticle, (state: GoodsToOrderFormReducerState, action: any) => {
                myAssert(() => state.bareGoodsEntities !== null, 'There needs to be goods if it is being updated');

                const { index, article } = action.payload;

                const newBareGoodsIndex = state.bareGoodsEntities?.findIndex((g) => g.index === index);

                if (newBareGoodsIndex !== undefined && newBareGoodsIndex > -1 && !!state.bareGoodsEntities) {
                    const newBareGoods = state.bareGoodsEntities[newBareGoodsIndex];

                    newBareGoods.article = article;

                    state.bareGoodsEntities = [
                        ...state.bareGoodsEntities.slice(0, newBareGoodsIndex),
                        newBareGoods,
                        ...state.bareGoodsEntities.slice(newBareGoodsIndex + 1),
                    ];
                }
            })
            .addCase(updateBareGoodsAvailableQuantity, (state: GoodsToOrderFormReducerState, action: any) => {
                myAssert(() => state.bareGoodsEntities !== null, 'There needs to be goods if it is being updated');

                const { index, availableQuantity } = action.payload;

                const newBareGoodsIndex = state.bareGoodsEntities?.findIndex((g) => g.index === index);

                if (newBareGoodsIndex !== undefined && newBareGoodsIndex > -1 && !!state.bareGoodsEntities) {
                    const newBareGoods = state.bareGoodsEntities[newBareGoodsIndex];

                    newBareGoods.availableQuantity = availableQuantity;

                    state.bareGoodsEntities = [
                        ...state.bareGoodsEntities.slice(0, newBareGoodsIndex),
                        newBareGoods,
                        ...state.bareGoodsEntities.slice(newBareGoodsIndex + 1),
                    ];
                }
            })
            .addCase(updateBareGoodsOrderedQuantity, (state: GoodsToOrderFormReducerState, action: any) => {
                myAssert(() => state.bareGoodsEntities !== null, 'There needs to be goods if it is being updated');

                const { index, orderedQuantity } = action.payload;

                const newBareGoodsIndex = state.bareGoodsEntities?.findIndex((g) => g.index === index);

                if (newBareGoodsIndex !== undefined && newBareGoodsIndex > -1 && !!state.bareGoodsEntities) {
                    const newBareGoods = state.bareGoodsEntities[newBareGoodsIndex];

                    newBareGoods.orderedQuantity = orderedQuantity;

                    state.bareGoodsEntities = [
                        ...state.bareGoodsEntities.slice(0, newBareGoodsIndex),
                        newBareGoods,
                        ...state.bareGoodsEntities.slice(newBareGoodsIndex + 1),
                    ];
                }
            })
            .addCase(setBareGoodsGoodsList, (state: GoodsToOrderFormReducerState, action: any) => {
                myAssert(() => state.bareGoodsEntities !== null, 'There needs to be goods if it is being updated');

                const { index, bareGoods } = action.payload;

                const newBareGoodsIndex = state.bareGoodsEntities?.findIndex((g) => g.index === index);

                if (newBareGoodsIndex !== undefined && newBareGoodsIndex > -1 && !!state.bareGoodsEntities) {
                    const newBareGoods = state.bareGoodsEntities[newBareGoodsIndex];

                    newBareGoods.goods = bareGoods;

                    state.bareGoodsEntities = [
                        ...state.bareGoodsEntities.slice(0, newBareGoodsIndex),
                        newBareGoods,
                        ...state.bareGoodsEntities.slice(newBareGoodsIndex + 1),
                    ];
                }
            });
    },
);
